import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { PostFilter, usePosts } from '../hooks/usePosts';
import { useSetCommunityPostPinned } from '../hooks/useCommunity';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { hasAnyRole } from '../utils/roles';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import {
  checkVideoUploadSize,
  MAX_FEED_IMAGE_UPLOAD_BYTES,
  MAX_VIDEO_UPLOAD_BYTES,
  formatBytesAsMb,
} from '../utils/mediaUploadLimits';
import { uploadVideoToBunny } from '../services/bunnyVideoService';

type PostStatus = 'published' | 'draft' | 'hidden';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;

  button {
    padding: 0.45rem 0.75rem;
    border-radius: 999px;
    border: 1px solid var(--outline);
    background: #fff;
    cursor: pointer;
    font-weight: 600;
    color: var(--onSurfaceVariant);
    transition: all 0.15s ease;
  }

  button.active {
    background: var(--primary);
    color: #fff;
    border-color: var(--primary);
  }
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }

  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.75rem;
      font-size: 0.85rem;
    }
  }
`;

const FormCard = styled.div`
  padding: 1.25rem;
  border: 1px solid var(--outline);
  border-radius: 14px;
  margin-bottom: 1.5rem;
  background: var(--surface);
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 0.9rem;
`;

const Label = styled.label`
  font-weight: 600;
  color: #1f2937;
`;

const TextArea = styled.textarea`
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  min-height: 120px;
`;

const Select = styled.select`
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
`;

const PrimaryButton = styled.button`
  background-color: var(--primary);
  color: #fff;
  padding: 0.6rem 1.1rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  box-shadow: 0 10px 18px rgba(31, 117, 254, 0.25);

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ActionButton = styled.button`
  padding: 0.35rem 0.6rem;
  border-radius: 999px;
  border: 1px solid var(--outline);
  background: #fff;
  color: var(--onSurface);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: var(--onSurfaceVariant);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DangerButton = styled(ActionButton)`
  border-color: #fecaca;
  color: var(--error);

  &:hover {
    border-color: #fca5a5;
  }
`;

const ErrorText = styled.p`
  color: #e53e3e;
  margin: 0.25rem 0 0;
`;

const UploadRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const HelperText = styled.span`
  color: #6b7280;
  font-size: 0.85rem;
`;

const MediaCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const MediaButton = styled.button`
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
`;

const MediaThumb = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--outline);
`;

const VideoPlaceholder = styled.div`
  width: 72px;
  height: 56px;
  border-radius: 8px;
  border: 1px solid var(--outline);
  background: var(--onSurface);
  color: var(--outline);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const MediaOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const MediaModal = styled.div`
  position: relative;
  width: min(1200px, 92vw);
  height: 86vh;
  background: var(--onSurface);
  border-radius: 12px;
  overflow: hidden;
`;

const MediaModalContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: var(--onSurface);

  img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
  }

  video,
  iframe {
    width: 100%;
    height: 100%;
  }

  video {
    object-fit: contain;
    background: var(--onSurface);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(15, 23, 42, 0.8);
  color: #fff;
  border: none;
  border-radius: 999px;
  width: 32px;
  height: 32px;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
`;

export const PostsPage: React.FC = () => {
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<PostFilter>('all');
  const [showForm] = useState(false);
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [videoLink, setVideoLink] = useState('');
  const [videoId, setVideoId] = useState('');
  const [typeValue, setTypeValue] = useState('post');
  const [status, setStatus] = useState<PostStatus>('published');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<{ url: string; type: 'image' | 'video' } | null>(null);
  const imageInputRef = React.useRef<HTMLInputElement | null>(null);
  const videoInputRef = React.useRef<HTMLInputElement | null>(null);
  const { data, isLoading, error } = usePosts(filter);
  const setPostPinned = useSetCommunityPostPinned();
  const canPinWall = hasAnyRole(profile?.roles ?? user?.user_metadata?.roles, ['admin', 'super_admin']);

  const createPost = useMutation({
    mutationFn: async () => {
      const trimmedContent = content.trim();
      const trimmedImage = imageUrl.trim();
      const trimmedVideo = videoLink.trim();
      const trimmedVideoId = videoId.trim();
      const trimmedType = typeValue.trim();

      if (!trimmedContent) throw new Error('Content is required to post.');

      const payload: Record<string, any> = {
        content: trimmedContent,
        status,
        type: trimmedType || null,
      };

      if (trimmedImage) payload.images = [trimmedImage];
      if (trimmedVideo) {
        payload.video_url = trimmedVideo;
        payload.video_link = trimmedVideo;
      }
      if (trimmedVideoId) {
        payload.video_id = trimmedVideoId;
        payload.video_processing_status = 'processing';
      }
      if (user?.id) payload.author_id = user.id;

      const { error: insertError } = await supabase.from('wall_posts').insert([payload]);
      if (insertError) throw insertError;
    },
    onSuccess: () => {
      setContent('');
      setImageUrl('');
      setVideoLink('');
      setVideoId('');
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createPost.mutate();
  };

  const hidePost = useMutation({
    mutationFn: async (postId: string) => {
      const { error: updateError } = await supabase
        .from('wall_posts')
        .update({ status: 'hidden' })
        .eq('id', postId);
      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });

  const unhidePost = useMutation({
    mutationFn: async (postId: string) => {
      const { error: updateError } = await supabase
        .from('wall_posts')
        .update({ status: 'published' })
        .eq('id', postId);
      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });

  const typeOptions = [
    { label: 'General Post', value: 'post' },
    { label: 'Praise Report', value: 'praise_report' },
    { label: 'Prayer Request', value: 'prayer' },
    { label: 'Testimony', value: 'testimony' },
    { label: 'Annoucement', value: 'announcement' },
    { label: 'Spotlight', value: 'spotlight' },
  ];

  const uploadToStorage = async (file: File) => {
    const ext = file.name.split('.').pop() || 'bin';
    const fileName = `posts/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('post-images').upload(fileName, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleImageUpload = async (file?: File) => {
    if (!file) return;
    if (file.size > MAX_FEED_IMAGE_UPLOAD_BYTES) {
      window.alert(
        `Please upload an image smaller than ${formatBytesAsMb(MAX_FEED_IMAGE_UPLOAD_BYTES)}MB.`
      );
      return;
    }
    setUploadingImage(true);
    try {
      const url = await uploadToStorage(file);
      setImageUrl(url);
    } catch (err) {
      console.error(err);
      window.alert('Image upload failed. Please try again.');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleVideoUpload = async (file?: File) => {
    if (!file) return;
    const sizeCheck = checkVideoUploadSize(file.size);
    if (!sizeCheck.ok) {
      window.alert(sizeCheck.reason);
      return;
    }
    if (!user?.id) {
      window.alert('You must be signed in to upload a video.');
      return;
    }
    setUploadingVideo(true);
    try {
      const uploadResult = await uploadVideoToBunny(
        file,
        {
          title: content.trim() || file.name || 'Community video',
          description: content.trim() || 'Video uploaded from Posts moderation',
          category: 'Community',
        },
        user.id
      );
      if (!uploadResult.success || !uploadResult.data) {
        throw new Error(uploadResult.error || 'Failed to upload video to Bunny.');
      }
      setVideoLink(uploadResult.data.videoUrl);
      setVideoId(uploadResult.data.videoId);
    } catch (err) {
      console.error(err);
      window.alert(err instanceof Error ? err.message : 'Video upload failed. Please try again.');
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  return (
    <Page>
      <HeaderRow>
        <HeaderTitle>Posts Moderation</HeaderTitle>
      </HeaderRow>

      {showForm && (
        <FormCard>
          <h3 style={{ marginTop: 0 }}>Create a Post</h3>
          <form onSubmit={handleCreate}>
            <Field>
              <Label htmlFor="post-content">Content</Label>
              <TextArea
                id="post-content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Share an update with your community…"
                required
              />
            </Field>
            <Field>
              <Label htmlFor="post-image">Image (optional)</Label>
              <UploadRow>
                <input
                  ref={imageInputRef}
                  id="post-image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e.target.files?.[0])}
                  disabled={uploadingImage || createPost.isPending}
                />
                <HelperText>
                  {uploadingImage
                    ? 'Uploading…'
                    : `JPG/PNG under ${formatBytesAsMb(MAX_FEED_IMAGE_UPLOAD_BYTES)}MB`}
                </HelperText>
                {imageUrl ? <HelperText>Selected image: {imageUrl}</HelperText> : null}
              </UploadRow>
            </Field>
            <Field>
              <Label htmlFor="post-video">Video (optional)</Label>
              <UploadRow>
                <input
                  ref={videoInputRef}
                  id="post-video"
                  type="file"
                  accept="video/*"
                  onChange={(e) => handleVideoUpload(e.target.files?.[0])}
                  disabled={uploadingVideo || createPost.isPending}
                />
                <HelperText>
                  {uploadingVideo
                    ? 'Uploading…'
                    : `Video under ${formatBytesAsMb(MAX_VIDEO_UPLOAD_BYTES)}MB`}
                </HelperText>
                {videoLink ? <HelperText>Selected video: {videoLink}</HelperText> : null}
              </UploadRow>
            </Field>
            <Field>
              <Label htmlFor="post-type">Type</Label>
              <Select
                id="post-type"
                value={typeValue}
                onChange={(e) => setTypeValue(e.target.value)}
              >
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field>
              <Label htmlFor="post-status">Status</Label>
              <Select id="post-status" value={status} onChange={(e) => setStatus(e.target.value as PostStatus)}>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="hidden">Hidden</option>
              </Select>
            </Field>
            {createPost.error && <ErrorText>{createPost.error.message}</ErrorText>}
            <PrimaryButton type="submit" disabled={createPost.isPending || !content.trim()}>
              {createPost.isPending ? 'Posting…' : 'Post'}
            </PrimaryButton>
          </form>
        </FormCard>
      )}
      <Tabs>
        {(['all', 'flagged', 'hidden'] as PostFilter[]).map((f) => (
          <button key={f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </Tabs>
      {isLoading && <p>Loading posts…</p>}
      {error && <p>Error loading posts</p>}
      {!isLoading && !error && (
        <TableCard>
          <TableWrap>
            <Table>
              <thead>
              <tr>
                <th>Content</th>
                <th>Media</th>
                <th>Author</th>
                <th>Date</th>
                <th>Likes</th>
                <th>Comments</th>
                <th>Status</th>
                <th>Type</th>
                <th>Pinned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((p) => (
                <tr key={p.id}>
                  <td style={{ maxWidth: 300 }}>{p.content?.slice(0, 120)}</td>
                  <td>
                    <MediaCell>
                      {Array.isArray(p.images) &&
                        p.images.map((url) => (
                          <MediaButton key={url} onClick={() => setPreviewMedia({ url, type: 'image' })}>
                            <MediaThumb src={url} alt="Post attachment" />
                          </MediaButton>
                        ))}
                      {(() => {
                        const videoUrl = p.video_url ?? p.video_link;
                        if (!videoUrl) return null;
                        return (
                          <MediaButton onClick={() => setPreviewMedia({ url: videoUrl, type: 'video' })}>
                            <VideoPlaceholder>Video</VideoPlaceholder>
                          </MediaButton>
                        );
                      })()}
                      {!p.video_url &&
                      !p.video_link &&
                      (!Array.isArray(p.images) || p.images.length === 0)
                        ? '-'
                        : null}
                    </MediaCell>
                  </td>
                  <td>{(p as any).author_name}</td>
                  <td>{new Date(p.created_at).toLocaleDateString()}</td>
                  <td>{p.post_likes?.[0]?.count ?? 0}</td>
                  <td>{p.wall_comments?.[0]?.count ?? 0}</td>
                  <td>{p.status}</td>
                  <td>{p.type ?? '-'}</td>
                  <td>{p.is_pinned ? 'Yes' : 'No'}</td>
                  <td style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {canPinWall ? (
                      <ActionButton
                        onClick={() =>
                          setPostPinned.mutate({ postId: p.id, pinned: !Boolean(p.is_pinned) })
                        }
                        disabled={setPostPinned.isPending}
                      >
                        {p.is_pinned ? 'Unpin' : 'Pin'}
                      </ActionButton>
                    ) : null}
                    {filter === 'hidden' || p.status === 'hidden' ? (
                      <ActionButton
                        onClick={() => unhidePost.mutate(p.id)}
                        disabled={unhidePost.isPending}
                      >
                        Unhide
                      </ActionButton>
                    ) : (
                      <DangerButton
                        onClick={() => hidePost.mutate(p.id)}
                        disabled={hidePost.isPending}
                      >
                        Hide
                      </DangerButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            </Table>
          </TableWrap>
        </TableCard>
      )}
      {previewMedia && (
        <MediaOverlay onClick={() => setPreviewMedia(null)}>
          <MediaModal onClick={(event) => event.stopPropagation()}>
            <CloseButton aria-label="Close" onClick={() => setPreviewMedia(null)}>
              ×
            </CloseButton>
            <MediaModalContent>
              {previewMedia.type === 'image' ? (
                <img src={previewMedia.url} alt="Post attachment" />
              ) : (() => {
                const isEmbed =
                  previewMedia.url.includes('youtube.com') ||
                  previewMedia.url.includes('youtu.be') ||
                  previewMedia.url.includes('iframe.mediadelivery.net');

                if (isEmbed) {
                  const src = previewMedia.url.includes('youtu.be')
                    ? previewMedia.url.replace('youtu.be/', 'www.youtube.com/embed/')
                    : previewMedia.url.includes('watch?v=')
                      ? previewMedia.url.replace('watch?v=', 'embed/')
                      : previewMedia.url;
                  return (
                    <iframe
                      src={src}
                      title="Video"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      frameBorder={0}
                    />
                  );
                }

                return <video src={previewMedia.url} controls autoPlay />;
              })()}
            </MediaModalContent>
          </MediaModal>
        </MediaOverlay>
      )}
    </Page>
  );
};

export default PostsPage;
